import { useQueryClient } from "@tanstack/react-query";

import { api, sendRequest } from "services/restUtil";
import type { Exercise } from "services/exercises/model";
import { apiQuery } from "utils/queries";
import type { LibraryObject, LibraryObjectDetail, LibraryObjectType } from "./model";

/**
 * The `/library` API, as a reader sees it. Two nouns, kept apart on purpose:
 * a **library object** is one record — `libraryObjectQuery` and the whole index —
 * while **content** is a filtered list of them, which is what every list endpoint
 * answers with. Authoring lives in `./authoring`, the editor in `./editor`.
 */
export type ContentListResponse = { list: LibraryObject[]; total: number };

export const libraryObjectQuery = apiQuery<LibraryObjectDetail, { id: string }>({
  key: "/library/:id",
  queryFn: ({ params }) => fetchLibraryObject(params),
});

/** Re-reads whatever a page is showing about a library object, after something
 * changed what it says -- a tag, a bought upgrade, a completed lesson. */
export function useRefreshLibraryObject() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({ queryKey: libraryObjectQuery.queryKeyPrefix });
}

function fetchLibraryObject(args: { id: string }) {
  return api.get<LibraryObjectDetail>(`/library/${args.id}`);
}

export const libraryObjectsQuery = apiQuery<{ list: LibraryObject[] }>({
  key: "/library",
});

// The reader marks up the document it is handed — `setAttribute` on its sections and
// videos — so a cached one would come back to the next reader already marked.
export const libraryObjectBodyQuery = apiQuery<Document, { id: string }>({
  key: "/library/:id/content",
  queryFn: ({ params }) => fetchLibraryObjectBody(params),
  staleTime: 0,
  gcTime: 0,
});

async function fetchLibraryObjectBody(args: { id: string }) {
  const resp = await sendRequest("GET", `/library/${args.id}/content`, {
    headers: { Accept: "application/xml, text/html" },
  });
  const contentType = resp.headers.get("content-type") ?? "";
  if (!contentType.includes("html") && !contentType.includes("xml")) {
    throw new Error(
      "Unfortunately we don’t support this content type yet but we’ll add it very soon."
    );
  }
  const text = await resp.text();
  const mimeTime = contentType.includes("xml") ? "text/xml" : "text/html";
  return new DOMParser().parseFromString(text, mimeTime);
}

type ContentListParams = {
  type: Extract<LibraryObjectType, "lesson" | "course">;
  page?: number; // 1-based
  page_size?: number;
};

export const startedContentQuery = apiQuery<ContentListResponse, ContentListParams>({
  key: "/library/started",
});

export const completedContentQuery = apiQuery<ContentListResponse, ContentListParams>({
  key: "/library/completed",
});

// GET /library/favorite querystring, per the server's FavoritesLibraryObjectRequest.
// Note: no `type` filter — the server returns all favorites regardless of object type.
type FavoriteContentParams = {
  sort_by?: "key" | "date";
  order?: "asc" | "desc";
  page?: number; // 1-based
  page_size?: number;
};

export const favoriteContentQuery = apiQuery<
  ContentListResponse,
  FavoriteContentParams | void
>({
  key: "/library/favorite",
});

export const featuredContentQuery = apiQuery<ContentListResponse>({
  key: "/library/featured",
});

// CloudSearch, so `page` and `sort_by` are required — omitting either 400s — and an
// empty query lists alphabetically.
type ContentSearchParams = {
  page?: number;
  size?: number;
  query?: string | null;
  sort_by?: string;
  include_lesson?: boolean;
  include_course?: boolean;
  tags?: string;
};

export const contentSearchQuery = apiQuery<ContentListResponse, ContentSearchParams>({
  key: "/library/course/search",
});

type ContentUsageParams = { category: string; from?: string; to?: string };
export type TagUsage = {
  tag: {
    id: string;
    name: string;
    description: string | null;
    category: string | null;
    order: number | null;
  };
  usage: number;
};

type ContentUsageResponse = {
  list: TagUsage[];
};

export function fetchContentUsage(params: ContentUsageParams) {
  return api.get<ContentUsageResponse>("/library/usage", params);
}

type ExercisesResponse = { list: Exercise[]; total: number };

// Named for its subject, not its answer: `services/exercises` has a
// `fetchExercisesBySource` of its own, which curries this one.
export function fetchLibraryObjectExercises(args: {
  id: string;
  page?: number;
  size?: number;
}) {
  return api.get<ExercisesResponse>(`/library/${args.id}/exercises`, {
    page: args.page,
    size: args.size,
  });
}

type TagPayload = { tags: string[] };

// The content editor's own tag writes. The admin surface tags an object through
// `/admin/library/tags` instead — see `addTagToLibraryObject` in `./admin`.
export function addLibraryObjectTags(args: { id: string; tags: string[] }) {
  return api.post<TagPayload>(`/library/${args.id}/tags`, { tags: args.tags });
}

export function removeLibraryObjectTags(args: { id: string; tags: string[] }) {
  return api.delete<TagPayload>(`/library/${args.id}/tags`, { tags: args.tags });
}
